/**
 * La paleta, derivada de tres colores — `ADR-015`.
 *
 * > [!info] Vive en `shared` y no en `ui`, y la razón es la API
 * > `contraste()` tiene que correr **también en el servidor**: una paleta se
 * > valida al guardarla, y una validación que sólo existe en el navegador no es
 * > una validación —quien mande el `PATCH` a mano se la salta—. `packages/ui`
 * > arrastra React y Base UI, y la API no puede importar eso.
 * >
 * > Aquí no hay ni una dependencia. Lo que toca el DOM se quedó en `ui`.
 *
 * > [!danger] Ningún componente escribe un color. Nunca.
 * > Todo pasa por los tokens que emite `aplicarPaleta`. Es lo único que hace que
 * > cambiar la paleta de una cafetería sea un cambio de **datos** y no una
 * > reingeniería: el día que el dueño elija sus tres colores, los componentes ya
 * > escritos no se tocan.
 * >
 * > Un `#hex` suelto en un componente es una excepción silenciosa a esa regla, y
 * > sólo se descubre cuando alguien elige un fondo oscuro y medio menú se vuelve
 * > ilegible. Hay una prueba que lo vigila.
 *
 * Los pasos derivados **no se guardan**: se calculan. El *handoff* de diseño
 * expresa sus grises como `rgba(43,33,26,.09)`, `rgba(43,33,26,.55)`… — el mismo
 * color a distintas opacidades. Guardarlos como catorce constantes obligaría a
 * reescribirlos todos al cambiar la tinta; derivarlos hace que se muevan solos.
 */
/* ── Color, sin biblioteca ────────────────────────────────────────────────── */
const aRgb = (hex) => {
    const h = hex.trim().replace('#', '');
    const c = h.length === 3 ? h.split('').map((x) => x + x).join('') : h;
    return [
        Number.parseInt(c.slice(0, 2), 16),
        Number.parseInt(c.slice(2, 4), 16),
        Number.parseInt(c.slice(4, 6), 16),
    ];
};
const aHex = (rgb) => '#' + rgb.map((v) => Math.round(Math.min(255, Math.max(0, v)))
    .toString(16).padStart(2, '0')).join('').toUpperCase();
/**
 * Aplana una opacidad sobre el suelo y devuelve un color **opaco**.
 *
 * El diseño usa `rgba(...)` sobre crema. Con el fondo en manos del dueño, esa
 * misma opacidad sobre un suelo oscuro da otro color — y ahí está el trabajo:
 * mezclar contra el fondo REAL, no contra el que tenía el diseñador.
 */
const mezclar = (color, suelo, proporcion) => {
    const [r1, g1, b1] = aRgb(color);
    const [r2, g2, b2] = aRgb(suelo);
    const p = Math.min(1, Math.max(0, proporcion));
    return aHex([r1 * p + r2 * (1 - p), g1 * p + g2 * (1 - p), b1 * p + b2 * (1 - p)]);
};
/** El mismo color con transparencia, en la sintaxis que entiende cualquier motor. */
const conAlfa = (hex, alfa) => {
    const [r, g, b] = aRgb(hex);
    return `rgb(${r} ${g} ${b} / ${Math.round(alfa * 100)}%)`;
};
/** Luminancia relativa de WCAG 2.1. */
const luminancia = (hex) => {
    const canal = (v) => {
        const s = v / 255;
        return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    };
    const [r, g, b] = aRgb(hex);
    return 0.2126 * canal(r) + 0.7152 * canal(g) + 0.0722 * canal(b);
};
/**
 * Razón de contraste entre dos colores, de 1 a 21.
 *
 * Es la función que sostiene el aviso del panel y `V-37`. Con `ADR-015` el aviso
 * **no bloquea**, así que esto no impide nada: sólo informa. Por eso el número
 * tiene que ser correcto — es lo único que hay.
 */
export const contraste = (a, b) => {
    const [x, y] = [luminancia(a), luminancia(b)].sort((p, q) => q - p);
    return Number((((x + 0.05) / (y + 0.05))).toFixed(2));
};
/** El mínimo de la casa para texto de cuerpo. `Estandares.md`. */
export const CONTRASTE_MINIMO = 4.5;
/**
 * Aleja un color de su suelo hasta que se lea, sin cambiar de tono.
 *
 * Se usa **sólo en los pasos derivados**, nunca en los tres que eligió la
 * cafetería: `ADR-015` decidió que sus colores se respetan y que lo que hay
 * contra una mala elección es un aviso. Pero el cuerpo secundario **no lo
 * eligió nadie**: lo fabricamos nosotros mezclando, y con una tinta pálida esa
 * mezcla sale a 1.7:1. Entregar eso sería un defecto nuestro, no una decisión
 * del dueño.
 *
 * Camina en pasos pequeños hacia el negro o el blanco —el que aleje del suelo—
 * y para en cuanto cumple. Así conserva el tono todo lo que puede.
 *
 * > [!danger] El destino se elige MIDIENDO, no por un umbral de luminancia
 * > Hasta el 2026-09-26 era `luminancia(suelo) > 0.5 ? negro : blanco`, y ese
 * > umbral está en el sitio equivocado: el punto donde las dos polaridades
 * > empatan es **0.179**, no 0.5. En la franja de en medio la regla caminaba
 * > hacia el blanco teniendo el negro a mano, y cuanto más cerca de 0.5, peor —
 * > con un fondo de luminancia 0.49 el blanco topa en **1.9:1** y el negro daba
 * > 10.8:1. Devolvía, literalmente, un texto ilegible habiendo uno legible.
 * >
 * > Es el mismo error que `textoLegibleSobre` tenía y se le corrigió el
 * > 2026-09-09 —«decidía mirando unos colores y entregaba otros»—, y sobrevivió
 * > aquí porque **ninguna de las nueve paletas curadas tiene un fondo así**: las
 * > nueve son cremas y grises muy claros. Lo destapó `T-71`, que es la tarea que
 * > por fin deja a una cafetería escribir el fondo que quiera.
 */
const alejarHastaLeerse = (color, suelo, minimo) => {
    if (contraste(color, suelo) >= minimo)
        return color;
    const destino = contraste('#000000', suelo) >= contraste('#FFFFFF', suelo)
        ? '#000000'
        : '#FFFFFF';
    //  Diecinueve pasos y el vigésimo fuera del bucle, que **es el destino puro**:
    //  `mezclar(destino, color, 1)` devuelve `destino` exactamente. Escrito así, y
    //  no con un `return destino` de cortafuegos detrás de `paso <= 20`, porque al
    //  elegir la polaridad midiendo ese cortafuegos se volvió **inalcanzable**:
    //  para que ninguna de las dos polaridades puras llegue al mínimo haría falta
    //  un suelo con luminancia por debajo de 0.175 y por encima de 0.183 a la vez.
    //
    //  Lo destapó el umbral de cobertura del 100 % del paquete, que es para lo que
    //  está. Comprobado equivalente por fuerza bruta antes de tocarlo: 4096 suelos
    //  × 5 colores × los tres mínimos que se usan —4.5, 3 y 1.08—, **cero
    //  diferencias** entre las dos formas.
    for (let paso = 1; paso < 20; paso += 1) {
        const candidato = mezclar(destino, color, paso * 0.05);
        if (contraste(candidato, suelo) >= minimo)
            return candidato;
    }
    return destino;
};
/** Los dos únicos textos que se ponen encima de un color de marca. */
const CLARO = '#F7F2EA';
const OSCURO = '#1C1917';
/**
 * Claro u oscuro, el que se lea mejor encima.
 *
 * No se supone que sobre la tinta va texto claro: una cafetería puede elegir una
 * «tinta» pálida, y entonces el texto claro desaparece. Se calcula.
 *
 * > [!warning] Se compara con los colores que se DEVUELVEN
 * > La primera versión decidía comparando `#FFFFFF` contra `#111111` y devolvía
 * > `#F7F2EA` o `#1C1917` — decidía mirando unos colores y entregaba otros. Con
 * > tintas medias los dos valores se cruzan y elegía el peor. Lo cazó la prueba
 * > de paletas extremas el 2026-09-09.
 */
const textoLegibleSobre = (suelo) => {
    const elegido = contraste(CLARO, suelo) >= contraste(OSCURO, suelo) ? CLARO : OSCURO;
    // Y si ni el mejor de los dos llega, se empuja hasta el puro.
    //
    // `CLARO` y `OSCURO` son cálidos a propósito: un blanco puro sobre espresso
    // deslumbra. Pero hay suelos —un gris medio, que alguien va a elegir— donde
    // NINGUNA polaridad suave alcanza 4.5:1, y ahí el puro sí. Se paga la dureza
    // sólo en el caso que la necesita, y se conserva la suavidad en todos los
    // demás, que son casi todos.
    return alejarHastaLeerse(elegido, suelo, CONTRASTE_MINIMO);
};
/**
 * De tres colores a la paleta entera.
 *
 * Las proporciones salen del *handoff*: `.09` para filetes, `.55–.62` para
 * cuerpo secundario, `.72` para rótulos. Se conservan porque están medidas sobre
 * un diseño real; lo que cambia es contra qué suelo se mezclan.
 */
export function derivarPaleta(colores) {
    const { tinta, fondo, acento } = colores;
    return {
        tinta, fondo, acento,
        sobreTinta: textoLegibleSobre(tinta),
        sobreAcento: textoLegibleSobre(acento),
        // Cuerpo largo: descripciones de platillos. Es el paso que más texto lleva,
        // así que le toca el mínimo entero.
        tintaSuave: alejarHastaLeerse(mezclar(tinta, fondo, 0.72), fondo, CONTRASTE_MINIMO),
        // Rótulos y notas cortas. 3:1, como `--neutro-500` en `tokens.css`, que ya
        // dejó escrito «nunca cuerpo largo».
        tintaTenue: alejarHastaLeerse(mezclar(tinta, fondo, 0.55), fondo, 3),
        // Un separador tiene que verse sin competir. Si la tinta y el fondo están
        // demasiado cerca, el 9 % redondea al propio fondo y la lista se convierte
        // en un muro de palabras: entonces se fuerza el mínimo perceptible.
        filete: alejarHastaLeerse(mezclar(tinta, fondo, 0.09), fondo, 1.08),
        superficie: mezclar(tinta, fondo, 0.06),
        superficieFuerte: mezclar(acento, fondo, 0.14),
        hueco: mezclar(tinta, fondo, 0.13),
        sobrePlano: mezclar(fondo, tinta, 0.14),
        velo: conAlfa(tinta, 0.45),
    };
}
/* ── Las curadas ──────────────────────────────────────────────────────────── */
/**
 * Las ocho de `Sistema-de-diseno.md` siguen siendo el camino recomendado —
 * `ADR-015` añade la salida libre, no las sustituye — más `casa`, que es la del
 * *handoff* del 2026-09-09.
 *
 * **En código y no en la base**, por decisión del 2026-09-09: son parte del
 * sistema de diseño, y una paleta nueva se revisa como se revisa un componente.
 * El precio, dicho: añadir una es un despliegue.
 *
 * Las claves van en inglés porque `tenants.brand_palette` ya las guarda así
 * desde agosto. Cambiarlas costaría migrar datos para no ganar nada.
 */
export const PALETAS = {
    /**
     * La del *handoff* del 2026-09-09: espresso, crema y terracota.
     *
     * El acento **no** es el `#B4633A` del diseño: es un 6 % más oscuro. Medido al
     * escribir esto, `#B4633A` sobre el crema da **4.11:1**, y los precios van en
     * ese color a 13-14 px — que no es «texto grande», así que le toca 4.5:1.
     *
     * El *handoff* dice haber corregido los grises que fallaban y **mantiene la
     * regla**: «keep that floor». El acento se le pasó. `#A95D37` da 4.56:1 y a
     * simple vista es el mismo color.
     */
    casa: { tinta: '#231F1C', fondo: '#FDFAF6', acento: '#B4482E' },
    orange: { tinta: '#1C1917', fondo: '#FBFAF8', acento: '#C24E0C' },
    red: { tinta: '#1C1917', fondo: '#FBFAF8', acento: '#B42318' },
    amber: { tinta: '#2B211A', fondo: '#FBF7F0', acento: '#96631A' },
    green: { tinta: '#18211C', fondo: '#F8FBF8', acento: '#2C6E49' },
    teal: { tinta: '#16211F', fondo: '#F7FBFA', acento: '#1C6C68' },
    blue: { tinta: '#171C21', fondo: '#F8FAFB', acento: '#175CD3' },
    indigo: { tinta: '#1A1A21', fondo: '#F9F9FB', acento: '#4338CA' },
    plum: { tinta: '#211A20', fondo: '#FBF8FA', acento: '#9C3587' },
};
/** La que se usa cuando la cafetería no eligió, o eligió una clave que ya no existe. */
export const PALETA_POR_OMISION = 'casa';
export const paletaDeClave = (clave) => (clave !== null && clave !== undefined && clave in PALETAS)
    ? PALETAS[clave]
    : PALETAS[PALETA_POR_OMISION];
/* ── De paleta a CSS ──────────────────────────────────────────────────────── */
/**
 * El nombre CSS de cada token. Vive aquí, y no junto a quien lo aplica, porque
 * lo usan **dos** consumidores: el navegador, que las escribe en un elemento, y
 * el servidor, que las inyecta como texto en el HTML.
 *
 * Duplicar este mapa sería la forma más silenciosa de romper esto: una copia
 * gana un token, la otra no, y un color deja de aplicarse **sólo en uno de los
 * dos caminos** — que es justo el fallo que nadie reproduce.
 */
export const VARIABLES_CSS = {
    tinta: '--fr-tinta', fondo: '--fr-fondo', acento: '--fr-acento',
    sobreTinta: '--fr-sobre-tinta', sobreAcento: '--fr-sobre-acento',
    tintaSuave: '--fr-tinta-suave', tintaTenue: '--fr-tinta-tenue',
    filete: '--fr-filete', superficie: '--fr-superficie',
    superficieFuerte: '--fr-superficie-fuerte', hueco: '--fr-hueco',
    sobrePlano: '--fr-sobre-plano', velo: '--fr-velo',
};
/**
 * La paleta como bloque de declaraciones.
 *
 * El servidor la inyecta en el HTML **antes de que el navegador pinte**. Sin
 * eso, el comensal ve los colores por omisión y luego los de su cafetería: un
 * parpadeo de marca ajena en la primera impresión, que es la que cuenta.
 */
export const paletaComoCss = (paleta) => Object.entries(VARIABLES_CSS)
    .map(([clave, variable]) => `${variable}:${paleta[clave]}`)
    .join(';');
//# sourceMappingURL=paleta.js.map